/** One phone number or email address, optionally labelled ("Mobile", "Office"). */
export interface ContactMethod {
  value: string;
  label?: string | null;
}

/**
 * One row of the Contacts page, as `get_contacts` returns it.
 *
 * Either a project member (`userProfileId` set; `contactId` null until someone
 * enters details for them) or an outside contact (`userProfileId` null).
 *
 * `scope` "building" is an org owner/admin: one entry per building, listed
 * once under "All projects" in the Building view (`projectId` null there) and
 * with each project in Projects view (`projectId` = that project).
 */
export interface ContactRow {
  scope: "building" | "project";
  projectId: number | null;
  projectName: string | null;
  contactId: number | null;
  userProfileId: number | null;
  firstName: string | null;
  lastName: string | null;
  /** The member's login email — shown, never edited here. */
  accountEmail: string | null;
  avatarUrl: string | null;
  color: string | null;
  fullName: string;
  company: string | null;
  jobTitle: string | null;
  phones: ContactMethod[];
  emails: ContactMethod[];
  notes: string | null;
  /** Responsibilities: design_disciplines ids, the tags documents and issues carry. */
  disciplineIds: number[];
  isOrgAdmin: boolean;
  createdBy: number | null;
  createdByName: string | null;
  modifiedAt: string | null;
  /** False for an entry whose person has since lost access to the project. */
  hasAccess: boolean;
  /** Server-computed with the update policy's own rule. For a member with no
   *  entry yet it means the viewer may create one. */
  canEdit: boolean;
}

/** What the form writes. */
export interface ContactDraft {
  fullName: string;
  company: string;
  jobTitle: string;
  phones: ContactMethod[];
  emails: ContactMethod[];
  notes: string;
  disciplineIds: number[];
}

/** The migration (franklin-data-pipeline `project_contacts`) has not applied yet. */
export class ContactsUnavailableError extends Error {
  constructor() {
    super("Contacts are not available yet");
    this.name = "ContactsUnavailableError";
  }
}

/** The viewer does not have permission to read this contact list. */
export class ContactReadDeniedError extends Error {
  constructor() {
    super("This contact list cannot be viewed by you");
    this.name = "ContactReadDeniedError";
  }
}

/** The write reached no row: the viewer may not change this entry (any more). */
export class ContactNotEditableError extends Error {
  constructor() {
    super("This contact can no longer be changed by you");
    this.name = "ContactNotEditableError";
  }
}

/** The viewer could not create an entry in this project. */
export class ContactCreateDeniedError extends Error {
  constructor() {
    super("This contact cannot be added by you");
    this.name = "ContactCreateDeniedError";
  }
}

/** An edit did not apply; the row may have changed or become inaccessible. */
export class ContactEditConflictError extends Error {
  constructor() {
    super("This contact edit could not be applied");
    this.name = "ContactEditConflictError";
  }
}

/** The server did not provide a usable affected-row count. */
export class ContactWriteUnconfirmedError extends Error {
  constructor() {
    super("The contact write could not be confirmed");
    this.name = "ContactWriteUnconfirmedError";
  }
}

/** Someone else entered this member's details first. */
export class ContactAlreadyExistsError extends Error {
  constructor() {
    super("Details for this person already exist on this project");
    this.name = "ContactAlreadyExistsError";
  }
}
